import React, { useContext, useMemo } from 'react'

import { useEffect, useState } from "react";
import { useTheme } from "@hooks/useTheme";
import queryKeys from "@configs/react-query/query-key";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { TOAST, CHARACTER_LENGTH } from "@constants/index";
import Breadcrumb from "@components/UI/Breadcrumb";
import { PrimaryButton } from "@components/Buttons";
import { DualSelectBox } from "@components/UI";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import { PermissionContext } from "@contexts/PermissionContext";
import { formatDateTime, uppercase } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { formatUserNameWithId } from "@utils/userDisplay";
import { extractErrorCode } from "@utils/error";
import { CustomInput, CustomTextarea } from '@components/Forms';
import { CustomSelect } from "@components/UI";
import {
  getSbomTypeLabel,
  getSbomVersion,
  resolveSbomFormat,
  CREATABLE_SBOM_TYPES,
  SBOM_FORMAT_CYCLONEDX,
  SBOM_FORMAT_SPDX_2_3,
  SBOM_FORMAT_SPDX_3,
  ClaritySC,
  CREATABLE_SBOM_TYPE_OPTIONS,
  SYSTEM_DEFAULT_SBOM_NAME,
} from "@constants/sbom";
import { TabsContainer } from '@components/Tabs';
import SbomPreview from './Components/SbomPreview';
import JsonViewer from '@components/UI/JsonViewer';
import cyclonedxData from '@assets/data/cycloneDX-1.6.json';
import spdx3Data from "@assets/data/spdx-3.0.1.json";
import spdx23Data from "@assets/data/spdx-2.3.json";
import LoadingSpinner from '@components/UI/LoadingSpinner';

const TEST_ID = "sbom-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

const SbomDetail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { canEditSbom } = useContext(PermissionContext);
  const { sbomSeq } = useParams();
  const [loading, setLoading] = useState(false);
  const [codeList, setCodeList] = useState([]);
  const [sbomData, setSbomData] = useState(null);
  const [originalData, setOriginalData] = useState(null);


  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [movedToAvailableItems, setMovedToAvailableItems] = useState([]);
  const [selectedAvailableItems, setSelectedAvailableItems] = useState([]);
  const [selectedSelectedItems, setSelectedSelectedItems] = useState([]);

  const [activeTab, setActiveTab] = useState(0);

  const sbomFormat = useMemo(() => resolveSbomFormat(sbomData), [sbomData]);

  const previewDocumentTabs = useMemo(() => {
    if (!sbomFormat) return [];

    switch (sbomFormat) {
      case SBOM_FORMAT_CYCLONEDX:
        return [
          {
            label: t("sbom.tree-view"),
            content: <SbomPreview format={SBOM_FORMAT_CYCLONEDX} />,
          },
          {
            label: t("sbom.json-view"),
            content: <JsonViewer jsonData={cyclonedxData} />,
          },
        ];

      case SBOM_FORMAT_SPDX_2_3:
        return [
          {
            label: t("sbom.tree-view"),
            content: <SbomPreview format={SBOM_FORMAT_SPDX_2_3} />,
          },
          {
            label: t("sbom.json-view"),
            content: <JsonViewer jsonData={spdx23Data} />,
          },
        ];

      case SBOM_FORMAT_SPDX_3:
        return [
          {
            label: t("sbom.tree-view"),
            content: <SbomPreview format={SBOM_FORMAT_SPDX_3} />,
          },
          {
            label: t("sbom.json-view"),
            content: <JsonViewer jsonData={spdx3Data} />,
          },
        ];

      default:
        return [];
    }
  }, [sbomFormat, t]);

  const [formData, setFormData] = useState({
    sbomName: "",
    sbomTypeCd: ClaritySC,
    sbomItems: [],
    remark: "",
  })

  const {
    data: codesData,
    isLoading: isCodesLoading,
    error: codesError,
  } = useFetchData(
    [queryKeys.getCodeListForSbom],
    queryKeys.getCodeListForSbom,
    {
      method: "GET",
    },
    {},
    {},
  );

  const breadcrumbItems = [
    { label: t("sbom.management-title"), href: "/sbom" },
    { label: t("sbom.sbom-detail"), href: `/sbom/${sbomSeq}` },
  ];

  // API mutations
  const getSbomData = useMutationData(
    `${queryKeys.getSbomBySeq}/${sbomSeq}`,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setSbomData(response);
          setFormData(response);
        }
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/sbom" } });
        } else {
          useSuccessToast(TOAST.error, t("common.error"), t("sbom.fetch-error"));
        }
        console.error("Error getting sbom data:", error);
      },
    },
  );

  // API hook
  const updateSBOMList = useMutationData(
    queryKeys.updateSbomList,
    { method: "PUT" },
    {
      onSuccess: (response) => {
        useSuccessToast(TOAST.success, t("common.success"), t("sbom.update-success"));
        setLoading(false);
        // Refresh sbom data
        getSbomData.mutate();
      },
      onError: (error) => {
        console.error("Error during update sbom:", error);
        const errorCode = extractErrorCode(error, "sbom.update-error");
        useSuccessToast(TOAST.error, t("common.error"), t(errorCode));
        setLoading(false);
      },
    },
  );

  // Handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // DualSelectbox handlers
  const handleMoveToSelected = () => {
    if (selectedAvailableItems.length === 0) return;

    const newSelected = [...selectedItems, ...selectedAvailableItems];
    setSelectedItems(newSelected);

    // Remove moved items from available items
    const updatedAvailable = availableItems.filter(
      (item) => !selectedAvailableItems.some((selected) => selected.value === item.value),
    );
    setAvailableItems(updatedAvailable);

    // Remove items from movedToAvailableItems if they are moved back to selected
    const movedBackValues = selectedAvailableItems.map((item) => item.value);
    setMovedToAvailableItems((prev) => prev.filter((item) => !movedBackValues.includes(item)));

    // Clear selection
    setSelectedAvailableItems([]);
  };

  const handleMoveToAvailable = () => {
    if (selectedSelectedItems.length === 0) return;

    // Move selected items to the top of available items
    const newAvailable = [...selectedSelectedItems, ...availableItems];
    setAvailableItems(newAvailable);

    // Track moved items for deletion
    const movedItems = selectedSelectedItems.map((item) => item.value);
    setMovedToAvailableItems((prev) => [...prev, ...movedItems]);
    // Remove moved items from selected items
    const updatedSelected = selectedItems.filter(
      (item) => !selectedSelectedItems.some((selected) => selected.value === item.value),
    );
    setSelectedItems(updatedSelected);

    // Clear selection
    setSelectedSelectedItems([]);
  };

  const handleSelectAllAvailable = () => {
    if (availableItems.length === 0) return;

    // If all items are already selected, deselect all
    if (selectedAvailableItems.length === availableItems.length) {
      setSelectedAvailableItems([]);
    } else {
      // Otherwise, select all items
      setSelectedAvailableItems([...availableItems]);
    }
  };

  const handleSelectAllSelected = () => {
    if (selectedItems.length === 0) return;

    // If all items are already selected, deselect all
    if (selectedSelectedItems.length === selectedItems.length) {
      setSelectedSelectedItems([]);
    } else {
      // Otherwise, select all items
      setSelectedSelectedItems([...selectedItems]);
    }
  };

  const validateForm = () => {
    return (
      formData.sbomName?.trim() &&
      formData.sbomTypeCd?.trim() &&
      selectedItems.length > 0
    );
  };

  const isFormValid = () => {
    return validateForm();
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const dataToSave = [];
    // Add new selected items

    const updatedSbomItems = [];
    selectedItems.forEach((item) => {
      const existingItem = sbomData.sbomItems.find(
        (original) => `${original.item}#${original.verificationTool}` === item.value,
      );

      updatedSbomItems.push({
        sbomItemSeq: sbomData.db ? (existingItem ? existingItem.sbomItemSeq : null) : -2,
        sbomSeq: sbomData.sbomSeq || -2,
        item: item.value.split("#")[0],
        itemName: item.label,
        verificationTool: item.value.split("#")[1] || "",
        db: existingItem ? existingItem.db : false,
        delete: false,
      });
    });

    // Add deleted items
    sbomData.sbomItems.forEach((item) => {
      const itemValue = `${item.item}#${item.verificationTool}`;
      if (item.delete || movedToAvailableItems.includes(itemValue)) {
        updatedSbomItems.push({ ...item, delete: true });
      }
    });

    dataToSave.push({
      ...sbomData,
      sbomName: formData.sbomName.trim(),
      sbomTypeCd: formData.sbomTypeCd,
      remark: formData.remark?.trim() || "",
      sbomItems: updatedSbomItems,
      delete: false,
    });

    updateSBOMList.mutate(dataToSave);
  };

  // Utils
  const areEqual = (a, b) => {
    if (a.sbomName !== b.sbomName) return false;
    if (a.sbomTypeCd !== b.sbomTypeCd) return false;
    if ((a.remark || "") !== (b.remark || "")) return false;

    if (a.sbomItems.length !== b.sbomItems.length) return false;
    // If you want to ignore order.
    // const arrA = [...a.sbomItems].sort();
    // const arrB = [...b.sbomItems].sort();

    const arrA = [...a.sbomItems];
    const arrB = [...b.sbomItems];

    for (let i = 0; i < arrA.length; i++) {
      if (arrA[i] !== arrB[i]) {
        return false;
      }
    }

    return true;
  };

  // Hook
  useEffect(() => {
    if (sbomSeq) {
      getSbomData.mutate();
    }
  }, [sbomSeq]);

  useEffect(() => {
    if (codesData && !isCodesLoading && !codesError) {
      setCodeList(codesData);
      const mappedAvailable = codesData.map((item) => ({
        value: `${item.code}#${item.codeValue}`,
        label: `${item.codeName} <${item.codeValue}>`,
      }));
      setAvailableItems(mappedAvailable);
    } else if (codesError) {
      useSuccessToast(TOAST.error, t("common.error"), t("sbom.fetch-error"));
    }
  }, [codesData, isCodesLoading, codesError]);

  useEffect(() => {
    if (sbomData && codeList) {
      const selectedItemsList =
        sbomData.sbomItems
          ?.filter((item) => !item.delete)
          .map((item) => {
            const value = `${item.item}#${item.verificationTool}`;
            const labelFromCodeList = codeMap.get(value) || item.item;
            return {
              value,
              label: `${labelFromCodeList} <${item.verificationTool}>`,
            };
          }) || [];
      setSelectedItems(selectedItemsList);

      const selectedValues = selectedItemsList.map((item) => item.value);
      const updatedAvailable = codeList
        .filter((item) => !selectedValues.includes(`${item.code}#${item.codeValue}`))
        .map((item) => ({
          value: `${item.code}#${item.codeValue}`,
          label: `${item.codeName} <${item.codeValue}>`,
        }));
      setAvailableItems(updatedAvailable);
    }
  }, [sbomData, codeList]);

  // When you load sbomData, also set originalData
  useEffect(() => {
    if (sbomData) {
      setOriginalData({
        sbomName: sbomData.sbomName || "",
        sbomTypeCd: sbomData.sbomTypeCd || "",
        remark: sbomData.remark || "",
        sbomItems: (sbomData.sbomItems || [])
          .filter((item) => !item.delete)
          .map((item) => `${item.item}#${item.verificationTool}`),
      });
    }
  }, [sbomData]);

  // Check dirty state
  const isDirty = useMemo(() => {
    if (!originalData) return false;

    const current = {
      sbomName: formData.sbomName,
      sbomTypeCd: formData.sbomTypeCd,
      remark: formData.remark || "",
      sbomItems: selectedItems.map((item) => item.value),
    };

    return !areEqual(originalData, current);
  }, [originalData, formData, selectedItems]);

  const isNonEditableSbom = useMemo(() => {
    if (!sbomData) return true;
    return !CREATABLE_SBOM_TYPES.includes(sbomData.sbomTypeCd);
  }, [sbomData]);

  const isSystemDefaultSbom = useMemo(() => {
    if (sbomData) {
      return sbomData.sbomName === SYSTEM_DEFAULT_SBOM_NAME;
    }
    return false;
  }, [sbomData]);


  const codeMap = useMemo(() => {
    if (codeList) {
      return new Map(codeList.map((c) => [`${c.code}#${c.codeValue}`, c.codeName]));
    }
  }, [codeList]);

  if (getSbomData.isPending) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div className={`${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header */}
      <div className="mb-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center justify-between pb-5">
        <h1
          className={`text-2xl truncate max-w-[300px] font-bold ${colors.textPrimary}`}
          title={sbomData?.sbomName || ""}
        >
          {sbomData?.sbomName || "LOADING..."}
        </h1>

        {(!isNonEditableSbom && canEditSbom()) && (
          <div className="flex items-center space-x-3">
            <PrimaryButton data-testid={tid("save-button")} onClick={handleSave} disabled={!isFormValid() || loading || !isDirty}>
              {t("common.save-button")}
            </PrimaryButton>
          </div>
        )}
      </div>


      {/* Sbom Information Cards */}
      <div data-testid={tid("info")} className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary}`}>
        <div className="flex flex-col lg:flex-row">
          <div className={`w-full p-6 m`}>
            <h2 className={`text-lg font-bold ${colors.textPrimary} mb-4`}>
              {uppercase(t("sbom.sbom-information-title"))}
            </h2>

            <div className={`space-y-0`}>
              {(isNonEditableSbom || isSystemDefaultSbom || !canEditSbom()) ? (
                <>
                  <div className={`grid grid-cols-4 gap-8 py-4 pt-3`}>
                    <div data-testid={fieldContainerTestId("sbom-name")}>
                      <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("sbom-name")}>
                        {t("sbom.sbom-name-label")}
                      </span>
                      <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("sbom-name")}>
                        {sbomData?.sbomName || "---"}
                      </span>
                    </div>
                    <div data-testid={fieldContainerTestId("sbom-type")}>
                      <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("sbom-type")}>
                        {t("sbom.sbom-type")}
                      </span>
                      <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("sbom-type")}>
                        {getSbomTypeLabel(sbomData?.sbomTypeCd) || "---"}
                      </span>
                    </div>
                    {getSbomVersion(sbomData) && (
                      <div>
                        <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                          {t("sbom.sbom-type-version")}
                        </span>
                        <span className={`text-sm font-medium ${colors.textMessage}`}>
                          {getSbomVersion(sbomData)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Remark - Read Only */}
                  <div className="mt-4" data-testid={fieldContainerTestId("remark")}>
                    <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("remark")}>
                      {t("sbom.remark")}
                    </label>
                    <CustomTextarea
                      data-testid={fieldValueTestId("remark")}
                      rows={4}
                      value={sbomData?.remark || ""}
                      title={sbomData?.remark}
                      readOnly={true}
                    />
                  </div>
                </>
              ) : (
                <div>
                  <div className="grid grid-cols-3 gap-6">
                    {/* Sbom Name */}
                    <div className="col-span-2">
                      <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("sbom-name")}>
                        {t("sbom.sbom-name-label")} <span className={colors.textRequired}>*</span>
                      </label>
                      <input
                        data-testid={fieldInputTestId("sbom-name")}
                        type="text"
                        placeholder={t("sbom.sbom-name-placeholder")}
                        value={formData.sbomName}
                        onChange={(e) => handleInputChange("sbomName", e.target.value)}
                        title={formData.sbomName}
                        className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                        maxLength={1024}
                      />
                    </div>

                    {/* Sbom Type */}
                    <div className="relative">
                      <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                        {t("sbom.sbom-type")} <span className={colors.textRequired}>*</span>
                      </label>
                      <CustomSelect
                        value={CREATABLE_SBOM_TYPE_OPTIONS.find((option) => option.value === formData.sbomTypeCd) || null}
                        onChange={(option) => handleInputChange("sbomTypeCd", option?.value || "")}
                        options={CREATABLE_SBOM_TYPE_OPTIONS}
                        isDisabled={!canEditSbom()}
                        placeholder={t("sbom.select-sbom-type")}
                      />
                    </div>
                  </div>

                  {/* Remark */}
                  <div className="mt-4" data-testid={fieldContainerTestId("remark")}>
                    <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("remark")}>
                      {t("sbom.remark")}
                    </label>
                    <CustomTextarea
                      data-testid={fieldInputTestId("remark")}
                      rows={4}
                      placeholder={t("sbom.remark-placeholder") || ""}
                      value={formData.remark}
                      onChange={(e) => handleInputChange("remark", e.target.value)}
                      title={formData.remark}
                      maxLength={CHARACTER_LENGTH.REMARK}
                      resize="none"
                      className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                    />
                    <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("remark-char-count")}>
                      {(formData.remark || "").length}/{CHARACTER_LENGTH.REMARK}
                    </p>
                  </div>
                </div>
              )}

              {/* Single Row: All metadata fields in one line */}
              <div className={`grid grid-cols-4 gap-8 py-4 pt-6`}>
                <div>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.creator")}
                  </span>
                  {isNonEditableSbom ? (
                    <UserNameWithId name={sbomData?.createrName} id={sbomData?.createrId} layout="inline" />
                  ) : (
                    <CustomInput
                      type="text"
                      disabled={true}
                      value={formatUserNameWithId(sbomData?.createrName, sbomData?.createrId)}
                    />
                  )}
                </div>

                <div>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("bulletin.creation-date")}
                  </span>
                  {isNonEditableSbom ? (
                    <span className={`text-sm font-medium ${colors.textMessage}`}>
                      {formatDateTime(sbomData?.createdAt) || "---"}
                    </span>
                  ) : (
                    <CustomInput type="text" disabled={true} value={formatDateTime(sbomData?.createdAt) || ""} />
                  )}
                </div>

                <div>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.last-modified-by")}
                  </span>
                  {isNonEditableSbom ? (
                    <UserNameWithId name={sbomData?.updaterName} id={sbomData?.updaterId} layout="inline" />
                  ) : (
                    <CustomInput
                      type="text"
                      disabled={true}
                      value={formatUserNameWithId(sbomData?.updaterName, sbomData?.updaterId)}
                    />
                  )}
                </div>

                <div>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.last-modified-date")}
                  </span>
                  {isNonEditableSbom ? (
                    <span className={`text-sm font-medium ${colors.textMessage}`}>
                      {formatDateTime(sbomData?.updatedAt) || "---"}
                    </span>
                  ) : (
                    <CustomInput type="text" disabled={true} value={formatDateTime(sbomData?.updatedAt) || ""} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(previewDocumentTabs && previewDocumentTabs.length == 0) ? (
        // sbom item list
        <div className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary} mt-6`}>
          <div className="flex flex-col lg:flex-row">
            <div className={`w-full p-6 m`}>
              <DualSelectBox
                availableItems={availableItems}
                selectedItems={selectedItems}
                onMoveToSelected={handleMoveToSelected}
                onMoveToAvailable={handleMoveToAvailable}
                onSelectAllAvailable={handleSelectAllAvailable}
                onSelectAllSelected={handleSelectAllSelected}
                selectedAvailableItems={selectedAvailableItems}
                selectedSelectedItems={selectedSelectedItems}
                onAvailableSelectionChange={setSelectedAvailableItems}
                onSelectedSelectionChange={setSelectedSelectedItems}
                disabled={isNonEditableSbom || !canEditSbom()}
                height="450px"
                canEdit={!isNonEditableSbom && canEditSbom()}
              />
            </div>
          </div>
        </div>
      ) : (
        // document preview
        <div className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary} mt-6`}>
          <div className="flex flex-col lg:flex-row">
            <div className={`w-full p-6 m`}>
              <h2 className={`text-lg font-bold ${colors.textPrimary} mb-6`}>
                {uppercase(
                  sbomFormat === SBOM_FORMAT_CYCLONEDX
                    ? t("sbom.cyclonedx-document-preview")
                    : sbomFormat === SBOM_FORMAT_SPDX_2_3 || sbomFormat === SBOM_FORMAT_SPDX_3
                      ? t("sbom.spdx-document-preview")
                      : t("sbom.document-preview", "Document Preview")
                )}
              </h2>

              <TabsContainer
                tabs={previewDocumentTabs}
                defaultActiveTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SbomDetail
